/**
 * Links for the Help widget's "Call us" / "WhatsApp" tiles, from the market's
 * support numbers. One place for the rules Codex found missing one by one
 * (#1240):
 *  - a number is put in international form from THIS market's prefix and
 *    national digit count, not the default market's local-number regex, so
 *    0712 345 678 under +254 becomes +254712345678;
 *  - only a number that carries this market's prefix is linked, because
 *    resolveCountryConfig fills a missing number from the default market;
 *  - an already-international number in this market must have the
 *    market's national digit count after the prefix;
 *  - wa.me takes the international digits without "+";
 *  - anything that cannot be put in that form stays unlinked (plain tile).
 */
import { DEFAULT_COUNTRY, PLACEHOLDER_SUPPORT_NUMBERS } from '@/lib/countryConfig';

export interface SupportNumberRules {
  phonePrefix?: string;
  phoneNationalDigits?: number;
  support?: { phone?: string; whatsapp?: string };
}

/** International form (+CCNNN…) of a support number, or '' when it cannot be formed. */
export function toInternational(raw: string | undefined, rules: SupportNumberRules): string {
  const cleaned = (raw ?? '').replace(/[^\d+]/g, '');
  if (!cleaned) return '';
  const prefix = rules.phonePrefix ?? '';
  const national = rules.phoneNationalDigits;
  if (cleaned.startsWith('+')) {
    if (!/^\+\d{6,15}$/.test(cleaned)) return '';
    // Already international in this market: the subscriber part must be the
    // market's exact length too, or +254123 would become a dead link.
    if (prefix && national && cleaned.startsWith(prefix) && cleaned.length !== prefix.length + national) return '';
    return cleaned;
  }
  if (!prefix || !national) return '';
  if (cleaned.startsWith('0') && cleaned.length === national + 1) return prefix + cleaned.slice(1);
  if (!cleaned.startsWith('0') && cleaned.length === national) return prefix + cleaned;
  return '';
}

const isPlaceholder = (intl: string) =>
  PLACEHOLDER_SUPPORT_NUMBERS.some((n) => toInternational(n, DEFAULT_COUNTRY) === intl);

/** Whether a support number is missing or still the seed's stand-in (countryConfig). */
export function isPlaceholderSupportNumber(raw: string | undefined): boolean {
  if (!(raw ?? '').trim()) return true;
  const intl = toInternational(raw, DEFAULT_COUNTRY);
  return !!intl && isPlaceholder(intl);
}

export function supportContactLinks(rules: SupportNumberRules): { callHref?: string; whatsappHref?: string } {
  const ownMarket = (intl: string) =>
    !!intl && !isPlaceholder(intl) && (!rules.phonePrefix || intl.startsWith(rules.phonePrefix));
  const phone = toInternational(rules.support?.phone, rules);
  const whatsapp = toInternational(rules.support?.whatsapp, rules);
  return {
    callHref: ownMarket(phone) ? `tel:${phone}` : undefined,
    whatsappHref: ownMarket(whatsapp) ? `https://wa.me/${whatsapp.slice(1)}` : undefined,
  };
}
